import React, { useEffect, useState } from 'react';
import { adminGetClients, adminUpdateClient, adminCreateClient } from '../../api';

const s = {
    h1:    { fontSize: 22, fontWeight: 700, marginBottom: 16 },
    top:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
    panel: { background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,.06)', overflow: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 12, whiteSpace: 'nowrap' },
    th:    { textAlign: 'left', padding: '10px 12px', background: '#f9fafb', borderBottom: '2px solid #f3f4f6', color: '#6b7280', fontSize: 10, textTransform: 'uppercase', letterSpacing: .5, fontWeight: 700 },
    td:    { padding: '9px 12px', borderBottom: '1px solid #f9fafb', verticalAlign: 'middle' },
    btn:   { padding: '5px 12px', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600 },
    modal: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 },
    mbox:  { background: '#fff', borderRadius: 12, padding: 32, width: 440, boxShadow: '0 8px 32px rgba(0,0,0,.2)', maxHeight: '90vh', overflowY: 'auto' },
    mh:    { fontSize: 18, fontWeight: 700, marginBottom: 20 },
    lbl:   { display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, marginTop: 12, color: '#374151' },
    inp:   { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13 },
    mrow:  { display: 'flex', gap: 10, marginTop: 20 },
    mbtn:  { flex: 1, padding: '10px 0', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 },
    stat:  { display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' },
    statBox: { background: '#fff', borderRadius: 8, padding: '12px 18px', boxShadow: '0 1px 3px rgba(0,0,0,.06)', minWidth: 100 },
    statNum: { fontSize: 22, fontWeight: 800, color: '#1a1d23' },
    statLbl: { fontSize: 11, color: '#6b7280', marginTop: 2 },
};

const BADGE = (active) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 700,
    background: active ? '#d1fae5' : '#fee2e2',
    color: active ? '#065f46' : '#991b1b',
});

const PILL = (color, bg, text) => ({
    display: 'inline-block', padding: '2px 7px', borderRadius: 12, fontSize: 10, fontWeight: 700,
    background: bg, color: color,
});

function fmtDate(val) {
    if (!val) return '—';
    const d = new Date(val);
    return d.toLocaleDateString('en-GB') + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

export default function AdminClients() {
    const [clients, setClients]   = useState([]);
    const [loading, setLoading]   = useState(true);
    const [modal, setModal]       = useState(null);
    const [form, setForm]         = useState({});
    const [saving, setSaving]     = useState(false);
    const [search, setSearch]     = useState('');
    const [filterStatus, setFilterStatus]     = useState('all');
    const [filterReseller, setFilterReseller] = useState('all');
    const [sortCol, setSortCol]   = useState('id');
    const [sortDir, setSortDir]   = useState('desc');

    const load = () => {
        setLoading(true);
        adminGetClients()
            .then(r => setClients(Array.isArray(r.data) ? r.data : []))
            .catch(() => {})
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    const openEdit = (c) => {
        setForm({
            id: c.id, balance: c.balance, markup_pct: c.markup_pct,
            discount_pct: c.discount_pct,
            is_active: c.is_active, is_admin: c.is_admin, is_reseller: c.is_reseller,
            tier: c.tier || 'none',
        });
        setModal('edit');
    };
    const openCreate = () => {
        setForm({ username: '', email: '', password: '', markup_pct: 0, discount_pct: 0 });
        setModal('create');
    };

    const save = async () => {
        setSaving(true);
        try {
            if (modal === 'edit') {
                await adminUpdateClient(form.id, {
                    balance: form.balance, markup_pct: form.markup_pct,
                    discount_pct: form.discount_pct,
                    is_active: form.is_active, is_admin: form.is_admin, is_reseller: form.is_reseller,
                    tier: form.tier === 'none' ? null : form.tier,
                });
            } else {
                await adminCreateClient(form);
            }
            setModal(null);
            load();
        } catch (e) {
            alert(e.response?.data?.error || 'Error saving');
        } finally {
            setSaving(false);
        }
    };

    // Stats
    const totalBalance = clients.reduce((s, c) => s + parseFloat(c.balance || 0), 0);
    const totalSpent   = clients.filter(c => c.tier !== 'tester').reduce((s, c) => s + parseFloat(c.total_spent || 0), 0);
    const active       = clients.filter(c => c.is_active).length;

    // Filter
    const q = search.toLowerCase();
    const filtered = clients.filter(c => {
        if (q && !c.username?.toLowerCase().includes(q) && !c.email?.toLowerCase().includes(q) && !String(c.id).includes(q)) return false;
        if (filterStatus === 'active'    && !c.is_active) return false;
        if (filterStatus === 'suspended' &&  c.is_active) return false;
        if (filterReseller === 'reseller' && c.tier !== 'reseller') return false;
        if (filterReseller === 'partner' && c.tier !== 'partner') return false;
        if (filterReseller === 'elite' && c.tier !== 'elite') return false;
        if (filterReseller === 'tester' && c.tier !== 'tester') return false;
        if (filterReseller === 'yes' && !c.is_reseller) return false;
        if (filterReseller === 'no'  &&  c.is_reseller) return false;
        return true;
    });

    // Sort
    const COLS = [
        { label: 'ID',         key: 'id',             cmp: (a, b) => a.id - b.id },
        { label: 'Username',   key: 'username',        cmp: (a, b) => (a.username || '').localeCompare(b.username || '') },
        { label: 'Email',      key: 'email',           cmp: (a, b) => (a.email || '').localeCompare(b.email || '') },
        { label: 'Balance',    key: 'balance',         cmp: (a, b) => parseFloat(a.balance || 0) - parseFloat(b.balance || 0) },
        { label: 'Spent',      key: 'total_spent',     cmp: (a, b) => parseFloat(a.total_spent || 0) - parseFloat(b.total_spent || 0) },
        { label: 'Orders',     key: 'order_count',     cmp: (a, b) => (a.order_count || 0) - (b.order_count || 0) },
        { label: 'Status',     key: 'is_active',       cmp: (a, b) => (a.is_active ? 1 : 0) - (b.is_active ? 1 : 0) },
        { label: 'Created',    key: 'created_at',      cmp: (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0) },
        { label: 'Last Login', key: 'last_login_at',   cmp: (a, b) => new Date(a.last_login_at || 0) - new Date(b.last_login_at || 0) },
        { label: 'Discount',   key: 'discount_pct',    cmp: (a, b) => (a.discount_pct || 0) - (b.discount_pct || 0) },
        { label: 'Markup',     key: 'markup_pct',      cmp: (a, b) => (a.markup_pct || 0) - (b.markup_pct || 0) },
        { label: 'Tier',       key: 'tier',            cmp: (a, b) => (a.tier || '').localeCompare(b.tier || '') },
        { label: 'Admin',      key: 'is_admin',        cmp: (a, b) => (a.is_admin ? 1 : 0) - (b.is_admin ? 1 : 0) },
        { label: '',           key: null },
    ];

    const handleSort = (key) => {
        if (!key) return;
        if (sortCol === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortCol(key); setSortDir('asc'); }
    };

    const activeCmp = COLS.find(c => c.key === sortCol)?.cmp;
    const sorted = activeCmp
        ? [...filtered].sort((a, b) => sortDir === 'asc' ? activeCmp(a, b) : activeCmp(b, a))
        : filtered;

    return (
        <div>
            {/* Stats strip */}
            <div style={s.stat}>
                <div style={s.statBox}><div style={s.statNum}>{clients.length}</div><div style={s.statLbl}>Total clients</div></div>
                <div style={s.statBox}><div style={s.statNum}>{active}</div><div style={s.statLbl}>Active</div></div>
                <div style={s.statBox}><div style={{ ...s.statNum, color: '#059669' }}>€{totalBalance.toFixed(2)}</div><div style={s.statLbl}>Total balance</div></div>
                <div style={s.statBox}><div style={{ ...s.statNum, color: '#1d4ed8' }}>€{totalSpent.toFixed(2)}</div><div style={s.statLbl}>Total spent</div></div>
            </div>

            <div style={s.top}>
                <h1 style={s.h1}>Clients</h1>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <input
                        placeholder="Search ID, username, email…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={{ padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, width: 200 }}
                    />
                    <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                        style={{ padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, background: '#fff' }}>
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="suspended">Suspended</option>
                    </select>
                    <select value={filterReseller} onChange={e => setFilterReseller(e.target.value)}
                        style={{ padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, background: '#fff' }}>
                        <option value="all">All Types</option>
                        <option value="yes">All Resellers</option>
                        <option value="reseller">🟢 Reseller</option>
                        <option value="partner">🟣 Partner</option>
                        <option value="elite">⚫ Elite</option>
                        <option value="tester">🧪 Tester</option>
                        <option value="no">Normal (no tier)</option>
                    </select>
                    <button style={{ ...s.btn, background: '#6366f1', color: '#fff', padding: '7px 16px', fontSize: 13 }} onClick={openCreate}>
                        + Add Client
                    </button>
                </div>
            </div>

            <div style={s.panel}>
                {loading ? (
                    <p style={{ padding: 24, color: '#9ca3af' }}>Loading...</p>
                ) : (
                    <div className="og-table-wrap">
                    <table style={s.table}>
                        <thead>
                            <tr>
                                {COLS.map(col => (
                                    <th key={col.label + col.key}
                                        style={{ ...s.th, cursor: col.key ? 'pointer' : 'default', userSelect: 'none' }}
                                        onClick={() => handleSort(col.key)}
                                    >
                                        {col.label}
                                        {col.key && (
                                            <span style={{ marginLeft: 4, opacity: sortCol === col.key ? 1 : 0.3 }}>
                                                {sortCol === col.key ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}
                                            </span>
                                        )}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map(c => (
                                <tr key={c.id} style={{ cursor: 'default' }}>
                                    <td style={{ ...s.td, color: '#9ca3af', fontSize: 11 }}>{c.id}</td>
                                    <td style={{ ...s.td, fontWeight: 600, color: '#1a1d23' }}>{c.username}</td>
                                    <td style={{ ...s.td, color: '#6b7280' }}>{c.email}</td>
                                    <td style={{ ...s.td, fontWeight: 700, color: '#059669' }}>
                                        €{parseFloat(c.balance || 0).toFixed(2)}
                                    </td>
                                    <td style={{ ...s.td, color: '#1d4ed8', fontWeight: 600 }}>
                                        {c.tier === 'tester' ? <span style={{ color: '#9ca3af' }}>—</span> : `€${parseFloat(c.total_spent || 0).toFixed(2)}`}
                                    </td>
                                    <td style={{ ...s.td, color: '#6b7280' }}>{c.tier === 'tester' ? '—' : (c.order_count || 0)}</td>
                                    <td style={s.td}>
                                        <span style={BADGE(c.is_active)}>{c.is_active ? 'Active' : 'Suspended'}</span>
                                    </td>
                                    <td style={{ ...s.td, color: '#6b7280' }}>{fmtDate(c.created_at)}</td>
                                    <td style={{ ...s.td, color: c.last_login_at ? '#374151' : '#d1d5db' }}>
                                        {fmtDate(c.last_login_at)}
                                    </td>
                                    <td style={s.td}>
                                        {c.discount_pct > 0
                                            ? <span style={PILL('#065f46','#d1fae5')}>-{c.discount_pct}%</span>
                                            : <span style={{ color: '#d1d5db' }}>—</span>}
                                    </td>
                                    <td style={s.td}>
                                        {c.markup_pct > 0
                                            ? <span style={PILL('#92400e','#fef3c7')}>+{c.markup_pct}%</span>
                                            : <span style={{ color: '#d1d5db' }}>—</span>}
                                    </td>
                                    <td style={s.td}>
                                        {c.tier === 'elite'
                                            ? <span style={PILL('#92400e','rgba(245,158,11,0.15)')}>⚫ ELITE {c.elite_discount ? `${c.elite_discount}%` : ''}</span>
                                            : c.tier === 'partner'
                                            ? <span style={PILL('#5b21b6','rgba(139,92,246,0.12)')}>🟣 PARTNER</span>
                                            : c.tier === 'tester'
                                            ? <span style={PILL('#0369a1','rgba(14,165,233,0.12)')}>🧪 TESTER</span>
                                            : c.tier === 'reseller' || c.is_reseller
                                            ? <span style={PILL('#065f46','rgba(16,185,129,0.12)')}>🟢 RESELLER</span>
                                            : <span style={{ color: '#d1d5db' }}>—</span>}
                                    </td>
                                    <td style={s.td}>
                                        {c.is_admin
                                            ? <span style={PILL('#7c3aed','rgba(124,58,237,0.12)')}>ADMIN</span>
                                            : <span style={{ color: '#d1d5db' }}>—</span>}
                                    </td>
                                    <td style={s.td}>
                                        <button
                                            style={{ ...s.btn, background: '#f3f4f6', color: '#374151' }}
                                            onClick={() => openEdit(c)}
                                        >
                                            Edit
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                )}
            </div>

            {modal && (
                <div style={s.modal} onClick={e => e.target === e.currentTarget && setModal(null)}>
                    <div style={s.mbox}>
                        <div style={s.mh}>{modal === 'edit' ? `Edit Client #${form.id}` : 'Create Client'}</div>

                        {modal === 'create' && (
                            <>
                                <label style={s.lbl}>Username</label>
                                <input style={s.inp} value={form.username || ''} onChange={e => setForm(f => ({...f, username: e.target.value}))} />
                                <label style={s.lbl}>Email</label>
                                <input style={s.inp} type="email" value={form.email || ''} onChange={e => setForm(f => ({...f, email: e.target.value}))} />
                                <label style={s.lbl}>Password</label>
                                <input style={s.inp} type="password" value={form.password || ''} onChange={e => setForm(f => ({...f, password: e.target.value}))} />
                            </>
                        )}

                        <label style={s.lbl}>Balance (€)</label>
                        <input style={s.inp} type="number" step="0.01" value={form.balance ?? ''} onChange={e => setForm(f => ({...f, balance: e.target.value}))} />

                        <label style={s.lbl}>Markup % <span style={{ fontWeight: 400, color: '#9ca3af' }}>(add % to all prices for this client)</span></label>
                        <input style={s.inp} type="number" min="0" max="200" value={form.markup_pct ?? 0} onChange={e => setForm(f => ({...f, markup_pct: parseInt(e.target.value) || 0}))} />

                        <label style={s.lbl}>Discount % <span style={{ fontWeight: 400, color: '#9ca3af' }}>(subtract % from all prices)</span></label>
                        <input style={s.inp} type="number" min="0" max="100" value={form.discount_pct ?? 0} onChange={e => setForm(f => ({...f, discount_pct: parseInt(e.target.value) || 0}))} />

                        {modal === 'edit' && (
                            <>
                            <label style={s.lbl}>Tier</label>
                            <select style={s.inp} value={form.tier || 'none'} onChange={e => {
                                const t = e.target.value;
                                const autoDiscount = t === 'tester' ? 100 : t === 'reseller' ? 40 : null;
                                setForm(f => ({
                                    ...f,
                                    tier: t,
                                    is_reseller: t === 'reseller',
                                    ...(autoDiscount !== null ? { discount_pct: autoDiscount } : {}),
                                }));
                            }}>
                                <option value="none">— None (normal user)</option>
                                <option value="reseller">🟢 Reseller (auto 40% discount)</option>
                                <option value="partner">🟣 Partner</option>
                                <option value="elite">⚫ Elite</option>
                                <option value="tester">🧪 Tester (auto 100% discount)</option>
                            </select>

                            <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {[
                                    { key: 'is_active', label: 'Active',  desc: 'Can log in and place orders' },
                                    { key: 'is_admin',  label: 'Admin',   desc: 'Full access to admin panel' },
                                ].map(({ key, label, desc }) => (
                                    <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, background: form[key] ? '#f0f4ff' : '#f9fafb', border: `1px solid ${form[key] ? '#c7d2fe' : '#e5e7eb'}`, cursor: 'pointer' }}>
                                        <input type="checkbox" checked={!!form[key]} onChange={e => {
                                            const val = e.target.checked;
                                            setForm(f => ({...f, [key]: val}));
                                        }} />
                                        <div>
                                            <div style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>
                                            <div style={{ fontSize: 11, color: '#6b7280' }}>{desc}</div>
                                        </div>
                                    </label>
                                ))}
                            </div>
                            </>
                        )}

                        <div style={s.mrow}>
                            <button style={{ ...s.mbtn, background: '#f3f4f6', color: '#374151' }} onClick={() => setModal(null)}>Cancel</button>
                            <button style={{ ...s.mbtn, background: '#6366f1', color: '#fff' }} onClick={save} disabled={saving}>
                                {saving ? 'Saving…' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
